import {
  Add as AddIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  CloudUpload as SaveIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Grade as GradeIcon,
  Numbers as CounterIcon,
  TextFields as TextFieldsIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React, { useRef, useState } from 'react';
import { apiErrorMessage } from '../components/Parameters/NewParameterDialog';
import ParameterPicker from '../components/Parameters/ParameterPicker';
import { AppLang, useDirection } from '../context/DirectionContext';
import { translate as tr } from '../i18n/translations';
import apiService from '../services/api';
import { SharedParameter, Station, StationGearItem, StationInfoForEvaluator } from '../types/api';

// ── Types (mirror mobile/src/types/station.ts) ────────────────────────────────

export type StationType = 'ordered' | 'accumulative' | 'accumulative_2' | 'individual';

export interface AccumulativeCounter {
  counterId: string;
  name: string;
  unit: string;
  color?: string;
}

export interface AccumulativeConfig {
  multiRound: boolean;
  roundCount?: number;
  counters: AccumulativeCounter[];
  hasTextComments: boolean;
}

export interface StationParameter {
  parameterId: string;
  name: string;
  valueType: 'scale' | 'text';
  maxGrade?: number;
  required?: boolean;
  /** Bank parameter this scale param is linked to; the server refreshes name + maxGrade from it. */
  sharedParameterId?: string;
}

export interface StationEvalInfo {
  notes: string;
  limitation: string;
  /** Unlinked legacy names only — the server prepends the bank names of includedParameterIds. */
  includedParameters: string[];
  includedParameterIds: string[];
  // P2 station guidance — a fixed set of fields; numbers kept as strings while editing
  purpose: string;
  gear: StationGearItem[];
  timeLimitMinutes: string;
  space: string;
  minAssessors: string;
  safetyNotes: string;
}

export function emptyEvalInfo(overrides: Partial<StationEvalInfo> = {}): StationEvalInfo {
  return {
    notes: '', limitation: '', includedParameters: [], includedParameterIds: [],
    purpose: '', gear: [], timeLimitMinutes: '', space: '', minAssessors: '', safetyNotes: '',
    ...overrides,
  };
}

/** Evaluator info as stored on the API → the builder's editable draft (bank links resolved by stationToDraft). */
function evalInfoFromApi(info: StationInfoForEvaluator | null | undefined): StationEvalInfo {
  return emptyEvalInfo({
    notes: info?.notes ?? '',
    limitation: info?.limitation ?? '',
    purpose: info?.description ?? '',
    gear: info?.gear ?? [],
    timeLimitMinutes: info?.timeLimitMinutes != null ? String(info.timeLimitMinutes) : '',
    space: info?.space ?? '',
    minAssessors: info?.minAssessors != null ? String(info.minAssessors) : '',
    safetyNotes: info?.safetyNotes ?? '',
  });
}

const positiveInt = (v: string): number | undefined => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

export interface StationDraft {
  stationId: string;
  name: string;
  description: string;
  stationType: StationType;
  stationLevel: number;
  parameters: StationParameter[];
  accumulativeConfig: AccumulativeConfig;
  infoForEvaluator: StationEvalInfo;
  includeAssessorFreeScore: boolean;
}

// ── Templates ─────────────────────────────────────────────────────────────────

const TEMPLATES: Record<string, StationDraft> = {
  ordered: {
    stationId: 'station_sprints_ordered',
    name: 'ספרינטים',
    description: 'דרג את המועמדים מהטוב ביותר לפחות טוב',
    stationType: 'ordered',
    stationLevel: 1,
    parameters: [],
    accumulativeConfig: { multiRound: false, counters: [], hasTextComments: false },
    infoForEvaluator: emptyEvalInfo(),
    includeAssessorFreeScore: true,
  },
  accumulative: {
    stationId: 'station_debate_accumulative',
    name: 'מעקב דיון',
    description: 'ספור השתתפות ותרומות בדיון',
    stationType: 'accumulative',
    stationLevel: 2,
    parameters: [],
    accumulativeConfig: {
      multiRound: true,
      counters: [
        { counterId: 'spoke', name: 'דיבר', unit: 'פעמים', color: '#0EA5E9' },
        { counterId: 'correct', name: 'תשובות נכונות', unit: 'יחידות', color: '#8B5CF6' },
      ],
      hasTextComments: true,
    },
    infoForEvaluator: emptyEvalInfo(),
    includeAssessorFreeScore: true,
  },
  accumulative_2: {
    stationId: 'station_debate_accumulative_2',
    name: 'מעקב דיון (רשת הקשה)',
    description: 'ספור השתתפות ותרומות בדיון — פריסת רשת להקשה מהירה',
    stationType: 'accumulative_2',
    stationLevel: 2,
    parameters: [],
    accumulativeConfig: {
      multiRound: true,
      counters: [
        { counterId: 'spoke', name: 'דיבר', unit: 'פעמים', color: '#0EA5E9' },
        { counterId: 'correct', name: 'תשובות נכונות', unit: 'יחידות', color: '#8B5CF6' },
      ],
      hasTextComments: false,
    },
    infoForEvaluator: emptyEvalInfo(),
    includeAssessorFreeScore: true,
  },
  individual: {
    stationId: 'station_lecture_individual',
    name: 'הרצאות',
    description: 'הערך כל מועמד על פי פרמטרים בהרצאה',
    stationType: 'individual',
    stationLevel: 3,
    parameters: [
      { parameterId: 'param_confidence', name: 'בטחון עצמי', valueType: 'scale', maxGrade: 7, required: true },
      { parameterId: 'param_delivery', name: 'עמידה מול קהל', valueType: 'scale', maxGrade: 7, required: true },
      { parameterId: 'param_notes', name: 'הערות', valueType: 'text', required: false },
    ],
    accumulativeConfig: { multiRound: false, counters: [], hasTextComments: false },
    infoForEvaluator: emptyEvalInfo(),
    includeAssessorFreeScore: true,
  },
};

// ── Utilities ─────────────────────────────────────────────────────────────────

function cleanObject(obj: any): any {
  if (Array.isArray(obj)) return obj.map(cleanObject);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== '' && v !== null)
        .map(([k, v]) => [k, cleanObject(v)])
    );
  }
  return obj;
}

export function buildOutput(draft: StationDraft): any {
  const base: any = {
    legacyStationId: draft.stationId || undefined,
    name: draft.name || undefined,
    description: draft.description || undefined,
    stationType: draft.stationType,
    stationLevel: draft.stationLevel,
    includeAssessorFreeScore: draft.includeAssessorFreeScore,
  };
  if (draft.stationType === 'accumulative' || draft.stationType === 'accumulative_2') {
    const cfg = { ...draft.accumulativeConfig };
    if (!cfg.multiRound) delete cfg.roundCount;
    base.accumulativeConfig = cfg;
  }
  if (draft.stationType === 'individual') {
    base.parameters = draft.parameters.map(p => {
      const out: any = { parameterId: p.parameterId, name: p.name, valueType: p.valueType };
      if (p.valueType === 'scale' && p.sharedParameterId) out.sharedParameterId = p.sharedParameterId;
      if (p.valueType === 'scale' && p.maxGrade) out.maxGrade = p.maxGrade;
      if (p.required !== undefined) out.required = p.required;
      return out;
    });
  }
  const info = draft.infoForEvaluator;
  const gear = info.gear
    .map(g => ({ item: g.item.trim(), quantity: g.quantity > 0 ? g.quantity : 1 }))
    .filter(g => g.item);
  const out = {
    notes: info.notes.trim() || undefined,
    limitation: info.limitation.trim() || undefined,
    includedParameterIds: info.includedParameterIds.length ? info.includedParameterIds : undefined,
    includedParameters: info.includedParameters.length ? info.includedParameters : undefined,
    description: info.purpose.trim() || undefined,
    gear: gear.length ? gear : undefined,
    timeLimitMinutes: positiveInt(info.timeLimitMinutes),
    space: info.space.trim() || undefined,
    minAssessors: positiveInt(info.minAssessors),
    safetyNotes: info.safetyNotes.trim() || undefined,
  };
  const cleaned = cleanObject(base);
  // An explicit null (not an absent key, which the server reads as "unchanged") so emptying every
  // guidance field on an existing station clears what assessors see.
  cleaned.infoForEvaluator = Object.values(out).some(v => v !== undefined) ? cleanObject(out) : null;
  return cleaned;
}

/** One message per scale parameter not linked to the bank; saving is blocked while non-empty. */
export function validateParameterLinks(draft: StationDraft, lang: AppLang = 'en'): string[] {
  if (draft.stationType !== 'individual') return [];
  return draft.parameters.flatMap((p, i) =>
    p.valueType === 'scale' && !p.sharedParameterId
      ? [tr('param.unlinkedScale', lang).replace('{n}', String(i + 1)).replace('{name}', p.name || tr('param.unnamed', lang))]
      : [],
  );
}

/**
 * Stored includedParameters = bank names of includedParameterIds, then legacy names (server order).
 * Returns the legacy leftovers only. Matches by bank name so names of dropped links survive;
 * ponytail: positional fallback while the bank is not loaded yet.
 */
export function splitIncludedNames(names: string[], ids: string[], bank: SharedParameter[]): string[] {
  if (!bank.length) return names.slice(ids.length);
  const linked = new Set(ids.map(id => bank.find(b => b.id === id)?.name).filter(Boolean));
  return names.filter(n => !linked.has(n));
}

/** Station JSON (bank row or downloaded file) → builder draft. */
export function stationToDraft(src: any, bank: SharedParameter[]): StationDraft {
  const ids: string[] = Array.isArray(src.infoForEvaluator?.includedParameterIds) ? src.infoForEvaluator.includedParameterIds : [];
  const names: string[] = Array.isArray(src.infoForEvaluator?.includedParameters) ? src.infoForEvaluator.includedParameters : [];
  return {
    stationId: src.stationId ?? src.legacyStationId ?? '',
    name: src.name ?? '',
    description: src.description ?? '',
    stationType: src.stationType ?? 'ordered',
    stationLevel: src.stationLevel ?? 1,
    parameters: Array.isArray(src.parameters) ? src.parameters : [],
    accumulativeConfig: src.accumulativeConfig ?? { multiRound: false, counters: [], hasTextComments: false },
    infoForEvaluator: {
      ...evalInfoFromApi(src.infoForEvaluator),
      includedParameterIds: ids,
      includedParameters: splitIncludedNames(names, ids, bank),
    },
    includeAssessorFreeScore: src.includeAssessorFreeScore ?? true,
  };
}

const uid = () => `param_${Math.random().toString(36).slice(2, 7)}`;
const cid = () => `counter_${Math.random().toString(36).slice(2, 7)}`;

const defaultDraft = (): StationDraft => ({
  stationId: '',
  name: '',
  description: '',
  stationType: 'ordered',
  stationLevel: 1,
  parameters: [],
  accumulativeConfig: { multiRound: false, counters: [], hasTextComments: false },
  infoForEvaluator: emptyEvalInfo(),
  includeAssessorFreeScore: true,
});

const TYPE_COLOR: Record<string, string> = {
  ordered: '#6366F1',
  accumulative: '#0EA5E9',
  accumulative_2: '#0891B2',
  individual: '#8B5CF6',
};

// ── Color palette ─────────────────────────────────────────────────────────────

const COLOR_PALETTE = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#0ea5e9', '#f97316', '#ec4899', '#14b8a6', '#6366f1',
  '#84cc16', '#6b7280',
];

const ColorPicker: React.FC<{ value?: string; onChange: (c: string | undefined) => void }> = ({ value, onChange }) => {
  const { lang } = useDirection();
  return (
  <Box>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600, mb: 0.75 }}>{tr('sb.color', lang)}</Typography>
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
      <Box
        onClick={() => onChange(undefined)}
        sx={{
          width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
          border: '1.5px dashed #CBD5E1', bgcolor: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: !value ? '0 0 0 2px white, 0 0 0 4px #CBD5E1' : 'none',
          '&:hover': { transform: 'scale(1.2)' },
          fontSize: '0.6rem', color: '#CBD5E1', lineHeight: 1,
        }}
      >✕</Box>
      {COLOR_PALETTE.map(c => (
        <Box
          key={c}
          onClick={() => onChange(c)}
          sx={{
            width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
            boxShadow: value === c ? `0 0 0 2px white, 0 0 0 4px ${c}` : 'none',
            transition: 'transform 0.1s',
            '&:hover': { transform: 'scale(1.2)' },
          }}
        />
      ))}
    </Box>
  </Box>
);
};

// ── Section title ─────────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', mb: 1.5 }}>
    {children}
  </Typography>
);

// ── Counter editor (Accumulative) ─────────────────────────────────────────────

const COUNTER_COLOR = '#0EA5E9';

const CounterBlock: React.FC<{
  index: number;
  total: number;
  counter: AccumulativeCounter;
  onChange: (c: AccumulativeCounter) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}> = ({ index, total, counter, onChange, onRemove, onMove }) => {
  const { lang } = useDirection();
  return (
  <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: `3px solid ${COUNTER_COLOR}`, borderRadius: 2, p: 2, mb: 2, bgcolor: 'white' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CounterIcon sx={{ fontSize: 15, color: COUNTER_COLOR }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0F172A' }}>{tr('sb.counter', lang)}</Typography>
        <Chip label={`#${index + 1}`} size="small" sx={{ height: 17, fontSize: '0.63rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <IconButton size="small" onClick={() => onMove('up')} disabled={index === 0} sx={{ color: '#94A3B8' }}><ArrowUpIcon sx={{ fontSize: 15 }} /></IconButton>
        <IconButton size="small" onClick={() => onMove('down')} disabled={index === total - 1} sx={{ color: '#94A3B8' }}><ArrowDownIcon sx={{ fontSize: 15 }} /></IconButton>
        <IconButton size="small" onClick={onRemove} sx={{ color: '#F87171', '&:hover': { bgcolor: '#FEF2F2' } }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
      </Box>
    </Box>
    <TextField label={tr('sb.nameHe', lang)} size="small" value={counter.name} onChange={e => onChange({ ...counter, name: e.target.value })} sx={{ mb: 2 }} fullWidth placeholder={tr('sb.egSpoke', lang)} />
    <TextField label={tr('sb.unit', lang)} size="small" value={counter.unit} onChange={e => onChange({ ...counter, unit: e.target.value })} sx={{ mb: 2 }} fullWidth placeholder={tr('sb.egTimes', lang)} />
    <ColorPicker value={counter.color} onChange={c => onChange({ ...counter, color: c })} />
  </Paper>
);
};

// ── Parameter editor (Individual) ─────────────────────────────────────────────

const PARAM_COLORS = { scale: '#6366F1', text: '#10B981' };

const ParameterBlock: React.FC<{
  index: number;
  total: number;
  param: StationParameter;
  options: SharedParameter[];
  onChange: (p: StationParameter) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}> = ({ index, total, param, options, onChange, onRemove, onMove }) => {
  const { lang } = useDirection();
  const color = PARAM_COLORS[param.valueType];
  const Icon = param.valueType === 'scale' ? GradeIcon : TextFieldsIcon;
  return (
    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: `3px solid ${color}`, borderRadius: 2, p: 2, mb: 2, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 15, color }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0F172A' }}>
            {param.valueType === 'scale' ? tr('sb.scaleParam', lang) : tr('sb.textParam', lang)}
          </Typography>
          <Chip label={`#${index + 1}`} size="small" sx={{ height: 17, fontSize: '0.63rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <IconButton size="small" onClick={() => onMove('up')} disabled={index === 0} sx={{ color: '#94A3B8' }}><ArrowUpIcon sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={() => onMove('down')} disabled={index === total - 1} sx={{ color: '#94A3B8' }}><ArrowDownIcon sx={{ fontSize: 15 }} /></IconButton>
          <IconButton size="small" onClick={onRemove} sx={{ color: '#F87171', '&:hover': { bgcolor: '#FEF2F2' } }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
        </Box>
      </Box>
      {param.valueType === 'scale' ? (
        <Box sx={{ mb: 2 }}>
          <ParameterPicker
            value={param.sharedParameterId ?? null}
            options={options}
            legacyName={param.sharedParameterId ? undefined : param.name || undefined}
            onChange={p => onChange(p
              ? { ...param, sharedParameterId: p.id, name: p.name, maxGrade: p.maxGrade }
              : { ...param, sharedParameterId: undefined })}
          />
        </Box>
      ) : (
        <TextField label={tr('sb.nameHe', lang)} size="small" value={param.name} onChange={e => onChange({ ...param, name: e.target.value })} sx={{ mb: 2 }} fullWidth placeholder={tr('sb.egConfidence', lang)} />
      )}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120, mb: 2 }}>
          <InputLabel>{tr('sb.valueType', lang)}</InputLabel>
          <Select
            value={param.valueType}
            label={tr('sb.valueType', lang)}
            onChange={e => onChange(e.target.value === 'text'
              ? { ...param, valueType: 'text', maxGrade: undefined, sharedParameterId: undefined }
              : { ...param, valueType: 'scale' })}
          >
            <MenuItem value="scale">{tr('sb.scaleGrade', lang)}</MenuItem>
            <MenuItem value="text">{tr('sb.text', lang)}</MenuItem>
          </Select>
        </FormControl>
        {param.valueType === 'scale' && (
          <Typography data-testid="scale-readonly" sx={{ fontSize: '0.8rem', color: '#475569', mb: 2 }}>
            {tr('param.scale', lang)}: <span dir="ltr">{param.sharedParameterId && param.maxGrade ? `1–${param.maxGrade}` : '—'}</span>
          </Typography>
        )}
        <FormControlLabel
          control={<Switch checked={!!param.required} onChange={e => onChange({ ...param, required: e.target.checked })} size="small" />}
          label={tr('sb.required', lang)}
          sx={{ mb: 2 }}
        />
      </Box>
    </Paper>
  );
};

// ── Main StationBuilder ───────────────────────────────────────────────────────

const StationBuilder: React.FC = () => {
  const { lang } = useDirection();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [station, setStation] = useState<StationDraft>(defaultDraft());
  const [editingStationId, setEditingStationId] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveError, setSaveError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Station | null>(null);
  // Shared legacy rows (unit null) are read + clone only; ids that 403'd are remembered too (defence in depth).
  const [sharedIds, setSharedIds] = useState<Set<string>>(new Set());
  const [editingShared, setEditingShared] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: bankParams = [] } = useQuery<SharedParameter[]>({
    queryKey: ['parameters'],
    queryFn: () => apiService.getParameters(),
  });

  const isShared = (s: Station) => s.unit === null || sharedIds.has(s.id);
  const markShared = (id: string) => setSharedIds(prev => new Set(prev).add(id));

  const output = buildOutput(station);
  const linkErrors = validateParameterLinks(station, lang);
  const jsonStr = JSON.stringify(output, null, 2);

  const update = (patch: Partial<StationDraft>) => setStation(s => ({ ...s, ...patch }));
  const updateInfo = (patch: Partial<StationEvalInfo>) =>
    setStation(s => ({ ...s, infoForEvaluator: { ...s.infoForEvaluator, ...patch } }));
  const updateGear = (i: number, patch: Partial<StationGearItem>) =>
    updateInfo({ gear: station.infoForEvaluator.gear.map((g, j) => (j === i ? { ...g, ...patch } : g)) });
  const updateCfg = (patch: Partial<AccumulativeConfig>) =>
    setStation(s => ({ ...s, accumulativeConfig: { ...s.accumulativeConfig, ...patch } }));

  const addCounter = () =>
    updateCfg({ counters: [...station.accumulativeConfig.counters, { counterId: cid(), name: '', unit: '' }] });

  const updateCounter = (i: number, c: AccumulativeCounter) =>
    updateCfg({ counters: station.accumulativeConfig.counters.map((x, idx) => idx === i ? c : x) });

  const removeCounter = (i: number) =>
    updateCfg({ counters: station.accumulativeConfig.counters.filter((_, idx) => idx !== i) });

  const moveCounter = (i: number, dir: 'up' | 'down') => {
    const arr = [...station.accumulativeConfig.counters];
    const t = dir === 'up' ? i - 1 : i + 1;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    updateCfg({ counters: arr });
  };

  const addParameter = (type: 'scale' | 'text') =>
    update({ parameters: [...station.parameters, { parameterId: uid(), name: '', valueType: type, required: false }] });

  const updateParam = (i: number, p: StationParameter) =>
    update({ parameters: station.parameters.map((x, idx) => idx === i ? p : x) });

  const removeParam = (i: number) =>
    update({ parameters: station.parameters.filter((_, idx) => idx !== i) });

  const moveParam = (i: number, dir: 'up' | 'down') => {
    const arr = [...station.parameters];
    const t = dir === 'up' ? i - 1 : i + 1;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    update({ parameters: arr });
  };

  const applyTemplate = (key: string) => setStation(JSON.parse(JSON.stringify(TEMPLATES[key])));

  const copyJson = async () => {
    await navigator.clipboard.writeText(jsonStr);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const downloadJson = () => {
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${station.stationId || 'station-definition'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        setStation(stationToDraft(parsed, bankParams));
        setLoadError('');
      } catch {
        setLoadError(tr('sb.invalidJson', lang));
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Bank query ──────────────────────────────────────────────────────────────

  const { data: bankStations = [], isLoading: bankLoading } = useQuery<Station[]>({
    queryKey: ['bankStations'],
    queryFn: () => apiService.getBankStations(),
    enabled: activeTab === 1,
  });

  // ── Save/delete mutations ───────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (payload: { id: string | null; data: Partial<Station> }) =>
      payload.id
        ? apiService.updateStation(payload.id, payload.data)
        : apiService.createStation(payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankStations'] });
      setSaveMsg(tr('sb.saved', lang));
      setSaveError('');
      setStation(defaultDraft());
      setEditingStationId(null);
      setEditingShared(false);
    },
    onError: (err: any, payload) => {
      if (err?.response?.status === 403 && payload.id) {
        markShared(payload.id);
        setEditingShared(true);
      }
      setSaveError(apiErrorMessage(err, 'Save failed.'));
      setSaveMsg('');
    },
  });

  const cloneMutation = useMutation({
    mutationFn: (id: string) => apiService.cloneBankStations([id]),
    onSuccess: clones => {
      queryClient.invalidateQueries({ queryKey: ['bankStations'] });
      if (clones[0]) openBankStation(clones[0]);
      setSaveMsg(tr('station.cloned', lang));
    },
    onError: (err: any) => {
      setSaveError(apiErrorMessage(err, tr('station.cloneError', lang)));
      setSaveMsg('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteStation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankStations'] });
      if (editingStationId === deleteTarget?.id) {
        setEditingStationId(null);
        setStation(defaultDraft());
      }
      setDeleteTarget(null);
    },
    onError: (err: any, id) => {
      if (err?.response?.status === 403) markShared(id);
      setSaveError(apiErrorMessage(err, tr('sb.deleteFailed', lang)));
      setDeleteTarget(null);
    },
  });

  const handleSaveToBank = () => {
    if (!station.name.trim()) { setSaveError(tr('sb.nameRequired', lang)); return; }
    if (editingShared || linkErrors.length) return;
    setSaveError('');
    saveMutation.mutate({
      id: editingStationId,
      data: {
        ...output,
        isTemplate: true,
      },
    });
  };

  function openBankStation(s: Station) {
    setStation(stationToDraft(s, bankParams));
    setEditingStationId(s.id);
    setEditingShared(isShared(s));
    setSaveMsg('');
    setSaveError('');
    setActiveTab(0);
  }

  const handleNewStation = () => {
    setStation(defaultDraft());
    setEditingStationId(null);
    setEditingShared(false);
    setSaveMsg('');
    setSaveError('');
  };

  // Linked scale params of this station not yet highlighted (ids only — text params aren't linkable).
  const quickAdd = station.stationType === 'individual'
    ? station.parameters.filter((p, i, all) =>
      p.valueType === 'scale' && p.sharedParameterId &&
      !station.infoForEvaluator.includedParameterIds.includes(p.sharedParameterId) &&
      all.findIndex(q => q.sharedParameterId === p.sharedParameterId) === i)
    : [];

  const TYPE_CHIPS: [StationType, string, string][] = [
    ['ordered',        tr('stationType.ordered', lang),        TYPE_COLOR.ordered],
    ['accumulative',   tr('stationType.accumulative', lang),   TYPE_COLOR.accumulative],
    ['accumulative_2', tr('stationType.accumulative_2', lang), TYPE_COLOR.accumulative_2],
    ['individual',     tr('stationType.individual', lang),     TYPE_COLOR.individual],
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      {/* Page header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>{tr('nav.stationBuilder', lang)}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            {tr('sb.title', lang)}
            {editingStationId && (
              <Chip
                label={editingShared ? tr('station.shared', lang) : tr('sb.editingBank', lang)}
                size="small"
                color={editingShared ? 'warning' : 'primary'}
                sx={{ ml: 1.5, fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeTab === 0 && (
              <>
                {editingStationId && (
                  <Button variant="outlined" size="small" onClick={handleNewStation} sx={{ color: '#64748B', borderColor: '#CBD5E1' }}>{tr('sb.newStation', lang)}</Button>
                )}
                <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleLoadFile} />
                <Button variant="outlined" startIcon={<UploadIcon />} size="small" onClick={() => fileInputRef.current?.click()}>{tr('sb.loadJson', lang)}</Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} size="small" onClick={downloadJson}>{tr('sb.download', lang)}</Button>
                <Button
                  variant={copySuccess ? 'contained' : 'outlined'}
                  color={copySuccess ? 'success' : 'primary'}
                  startIcon={<CopyIcon />}
                  size="small"
                  onClick={copyJson}
                >
                  {copySuccess ? tr('sb.copied', lang) : tr('sb.copyJson', lang)}
                </Button>
                {!editingShared && (
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    size="small"
                    onClick={handleSaveToBank}
                    disabled={saveMutation.isPending || linkErrors.length > 0}
                    sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}
                  >
                    {saveMutation.isPending ? tr('common.saving', lang) : editingStationId ? tr('sb.updateInBank', lang) : tr('sb.saveToBank', lang)}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2.5, borderBottom: '1px solid #E2E8F0', '& .MuiTab-root': { fontWeight: 600, fontSize: '0.82rem', textTransform: 'none' } }}
      >
        <Tab label={tr('sb.tab.builder', lang)} />
        <Tab label={tr('sb.tab.bank', lang)} />
      </Tabs>

      {saveMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveMsg('')}>{saveMsg}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>}
      {loadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}

      {activeTab === 0 && editingShared && editingStationId && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              disabled={cloneMutation.isPending}
              onClick={() => cloneMutation.mutate(editingStationId)}
            >
              {cloneMutation.isPending ? tr('station.cloning', lang) : tr('station.cloneToBank', lang)}
            </Button>
          }
        >
          {tr('station.sharedNotice', lang)}
        </Alert>
      )}
      {activeTab === 0 && !editingShared && linkErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {linkErrors.map(m => <div key={m}>{m}</div>)}
        </Alert>
      )}

      {/* ── BUILDER TAB ── */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

          {/* LEFT: Config panels */}
          <Box sx={{ flex: '0 0 56%', minWidth: 0 }}>

            {/* Templates */}
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, mb: 2.5 }}>
              <SectionTitle>{tr('sb.quickStart', lang)}</SectionTitle>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[
                  ['ordered',        tr('sb.tpl.ordered', lang)],
                  ['accumulative',   tr('sb.tpl.accumulative', lang)],
                  ['accumulative_2', tr('sb.tpl.accumulative_2', lang)],
                  ['individual',     tr('sb.tpl.individual', lang)],
                ].map(([key, label]) => (
                  <Button key={key} variant="outlined" size="small" onClick={() => applyTemplate(key)}
                    sx={{ borderColor: '#E2E8F0', color: '#475569', '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#EEF2FF' } }}>
                    {label}
                  </Button>
                ))}
              </Box>
            </Paper>

            {/* Station Definition */}
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, mb: 2.5 }}>
              <SectionTitle>{tr('sb.definition', lang)}</SectionTitle>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <TextField
                  label={tr('sb.level', lang)}
                  size="small"
                  type="number"
                  value={station.stationLevel}
                  onChange={e => update({ stationLevel: Number(e.target.value) })}
                  inputProps={{ min: 0 }}
                  helperText={tr('sb.levelHint', lang)}
                  sx={{ width: 160 }}
                />
              </Box>
              <TextField
                label={tr('sb.name', lang)}
                size="small"
                value={station.name}
                onChange={e => update({ name: e.target.value })}
                sx={{ mb: 2 }}
                fullWidth
                placeholder={tr('sb.egSprints', lang)}
              />
              <TextField
                label={tr('sb.description', lang)}
                size="small"
                value={station.description}
                onChange={e => update({ description: e.target.value })}
                sx={{ mb: 2 }}
                fullWidth
                placeholder={tr('sb.descriptionHint', lang)}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#64748B' }}>{tr('sb.type', lang)}</Typography>
                {TYPE_CHIPS.map(([type, label, color]) => (
                  <Chip
                    key={type}
                    label={label}
                    size="small"
                    onClick={() => update({ stationType: type })}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: station.stationType === type ? color : 'transparent',
                      color: station.stationType === type ? '#FFF' : '#475569',
                      border: `1.5px solid ${station.stationType === type ? color : '#E2E8F0'}`,
                      '&:hover': { bgcolor: station.stationType === type ? color : '#F8FAFC' },
                    }}
                  />
                ))}
              </Box>

              <FormControlLabel
                sx={{ mt: 2, ml: 0 }}
                control={
                  <Switch
                    checked={station.includeAssessorFreeScore}
                    onChange={e => update({ includeAssessorFreeScore: e.target.checked })}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#0F172A' }}>{tr('sb.freeScore', lang)}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{tr('sb.freeScoreHint', lang)}</Typography>
                  </Box>
                }
              />
            </Paper>

            {/* Evaluator Info */}
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: '3px solid #F59E0B', borderRadius: 3, p: 2.5, mb: 2.5 }}>
              <SectionTitle>{tr('sb.evalInfo', lang)}</SectionTitle>
              <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mb: 2, mt: -1 }}>{tr('sb.evalInfoHint', lang)}</Typography>
              {/* Guidance (P2): purpose, gear and a fixed set of constraints */}
              <TextField
                label={tr('sb.purpose', lang)}
                size="small"
                value={station.infoForEvaluator.purpose}
                onChange={e => updateInfo({ purpose: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                placeholder={tr('sb.egPurpose', lang)}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#64748B', mb: 1 }}>{tr('guide.gear', lang)}</Typography>
              {station.infoForEvaluator.gear.map((g, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    label={tr('guide.gearItem', lang)}
                    size="small"
                    value={g.item}
                    onChange={e => updateGear(i, { item: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label={tr('guide.quantity', lang)}
                    size="small"
                    type="number"
                    value={g.quantity}
                    onChange={e => updateGear(i, { quantity: parseInt(e.target.value, 10) || 0 })}
                    inputProps={{ min: 1 }}
                    sx={{ width: 90 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => updateInfo({ gear: station.infoForEvaluator.gear.filter((_, j) => j !== i) })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => updateInfo({ gear: [...station.infoForEvaluator.gear, { item: '', quantity: 1 }] })}
                sx={{ mb: 2 }}
              >{tr('guide.addItem', lang)}</Button>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
                <TextField
                  label={tr('guide.timeLimit', lang)}
                  size="small"
                  type="number"
                  value={station.infoForEvaluator.timeLimitMinutes}
                  onChange={e => updateInfo({ timeLimitMinutes: e.target.value })}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label={tr('guide.space', lang)}
                  size="small"
                  value={station.infoForEvaluator.space}
                  onChange={e => updateInfo({ space: e.target.value })}
                  placeholder={tr('sb.egSpace', lang)}
                />
                <TextField
                  label={tr('guide.minAssessors', lang)}
                  size="small"
                  type="number"
                  value={station.infoForEvaluator.minAssessors}
                  onChange={e => updateInfo({ minAssessors: e.target.value })}
                  inputProps={{ min: 1 }}
                />
              </Box>
              <TextField
                label={tr('sb.safety', lang)}
                size="small"
                value={station.infoForEvaluator.safetyNotes}
                onChange={e => updateInfo({ safetyNotes: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                placeholder={tr('sb.egSafety', lang)}
                helperText={tr('sb.safetyHint', lang)}
              />
              <TextField
                label={tr('sb.notes', lang)}
                size="small"
                value={station.infoForEvaluator.notes}
                onChange={e => update({ infoForEvaluator: { ...station.infoForEvaluator, notes: e.target.value } })}
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                placeholder={tr('sb.egNotes', lang)}
              />
              <TextField
                label={tr('sb.limitation', lang)}
                size="small"
                value={station.infoForEvaluator.limitation}
                onChange={e => update({ infoForEvaluator: { ...station.infoForEvaluator, limitation: e.target.value } })}
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                placeholder={tr('sb.egLimitation', lang)}
                helperText={tr('sb.limitationHint', lang)}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#64748B', mb: 1 }}>{tr('sb.highlighted', lang)}</Typography>
              <ParameterPicker
                multiple
                value={station.infoForEvaluator.includedParameterIds}
                options={bankParams}
                onChange={picked => update({
                  infoForEvaluator: {
                    ...station.infoForEvaluator,
                    includedParameterIds: picked.map(p => p.id),
                    // picking the bank version of a legacy name replaces it
                    includedParameters: station.infoForEvaluator.includedParameters.filter(n => !picked.some(p => p.name === n)),
                  },
                })}
              />
              {station.infoForEvaluator.includedParameters.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                  {station.infoForEvaluator.includedParameters.map(n => (
                    <Chip
                      key={n}
                      label={`${n} · ${tr('param.notLinkedShort', lang)}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      onDelete={() => update({
                        infoForEvaluator: {
                          ...station.infoForEvaluator,
                          includedParameters: station.infoForEvaluator.includedParameters.filter(x => x !== n),
                        },
                      })}
                      sx={{ fontSize: '0.72rem' }}
                    />
                  ))}
                </Box>
              )}
              {quickAdd.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mb: 0.75 }}>
                    {tr('param.quickAdd', lang)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {quickAdd.map(p => (
                      <Chip
                        key={p.parameterId}
                        label={`+ ${p.name}`}
                        size="small"
                        variant="outlined"
                        onClick={() => update({
                          infoForEvaluator: {
                            ...station.infoForEvaluator,
                            includedParameterIds: [...station.infoForEvaluator.includedParameterIds, p.sharedParameterId as string],
                            includedParameters: station.infoForEvaluator.includedParameters.filter(n => n !== p.name),
                          },
                        })}
                        sx={{ cursor: 'pointer', fontSize: '0.72rem', borderColor: '#CBD5E1', color: '#475569', '&:hover': { borderColor: '#6366F1', color: '#6366F1' } }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* ORDERED */}
            {station.stationType === 'ordered' && (
              <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: '3px solid #6366F1', borderRadius: 3, p: 2.5, mb: 2.5 }}>
                <SectionTitle>{tr('sb.orderedConfig', lang)}</SectionTitle>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <strong>{tr('sb.orderedNone', lang)}</strong> {tr('sb.orderedHint', lang)}
                </Alert>
              </Paper>
            )}

            {/* ACCUMULATIVE / ACCUMULATIVE V2 — identical config shape; V2 is a
                mobile UI variant only (tap-to-count grid instead of per-candidate
                cards), so it reuses this exact editor. See mobile Stations Guide
                § Accumulative V2. */}
            {(station.stationType === 'accumulative' || station.stationType === 'accumulative_2') && (
              <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: `3px solid ${TYPE_COLOR[station.stationType]}`, borderRadius: 3, p: 2.5, mb: 2.5 }}>
                <SectionTitle>{station.stationType === 'accumulative_2' ? tr('sb.acc2Config', lang) : tr('sb.accConfig', lang)}</SectionTitle>
                {station.stationType === 'accumulative_2' && (
                  <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                    {tr('sb.acc2Hint', lang)}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={station.accumulativeConfig.multiRound}
                        onChange={e => updateCfg({ multiRound: e.target.checked, roundCount: undefined })}
                        size="small"
                      />
                    }
                    label={tr('sb.multiRound', lang)}
                  />
                  {station.accumulativeConfig.multiRound && (
                    <TextField
                      label={tr('sb.roundCount', lang)}
                      size="small"
                      type="number"
                      value={station.accumulativeConfig.roundCount ?? ''}
                      onChange={e => updateCfg({ roundCount: e.target.value ? Number(e.target.value) : undefined })}
                      inputProps={{ min: 1 }}
                      sx={{ width: 220 }}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={station.accumulativeConfig.hasTextComments}
                        onChange={e => updateCfg({ hasTextComments: e.target.checked })}
                        size="small"
                        disabled={station.stationType === 'accumulative_2'}
                      />
                    }
                    label={tr('sb.textAnnotations', lang)}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#475569' }}>
                    {tr('sb.counters', lang)} ({station.accumulativeConfig.counters.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                    onClick={addCounter}
                    sx={{ fontSize: '0.72rem', color: COUNTER_COLOR, borderColor: COUNTER_COLOR, '&:hover': { bgcolor: '#F0F9FF' } }}
                  >{tr('sb.addCounter', lang)}</Button>
                </Box>
                {station.accumulativeConfig.counters.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed #E2E8F0', borderRadius: 2, color: '#94A3B8' }}>
                    <Typography sx={{ fontSize: '0.875rem' }}>{tr('sb.noCounters', lang)}</Typography>
                  </Box>
                ) : (
                  station.accumulativeConfig.counters.map((c, i) => (
                    <CounterBlock
                      key={i}
                      index={i}
                      total={station.accumulativeConfig.counters.length}
                      counter={c}
                      onChange={nc => updateCounter(i, nc)}
                      onRemove={() => removeCounter(i)}
                      onMove={dir => moveCounter(i, dir)}
                    />
                  ))
                )}
              </Paper>
            )}

            {/* INDIVIDUAL */}
            {station.stationType === 'individual' && (
              <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderLeft: '3px solid #8B5CF6', borderRadius: 3, p: 2.5, mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <SectionTitle>{tr('sb.parameters', lang)} ({station.parameters.length})</SectionTitle>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GradeIcon sx={{ fontSize: 13 }} />}
                      onClick={() => addParameter('scale')}
                      sx={{ fontSize: '0.72rem', color: '#6366F1', borderColor: '#6366F1', '&:hover': { bgcolor: '#EEF2FF' } }}
                    >{tr('sb.scale', lang)}</Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TextFieldsIcon sx={{ fontSize: 13 }} />}
                      onClick={() => addParameter('text')}
                      sx={{ fontSize: '0.72rem', color: '#10B981', borderColor: '#10B981', '&:hover': { bgcolor: '#F0FDF4' } }}
                    >{tr('sb.text', lang)}</Button>
                  </Box>
                </Box>

                {station.parameters.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed #E2E8F0', borderRadius: 2, color: '#94A3B8' }}>
                    <Typography sx={{ fontSize: '0.875rem' }}>{tr('sb.noParams', lang)}</Typography>
                  </Box>
                ) : (
                  station.parameters.map((p, i) => (
                    <ParameterBlock
                      key={i}
                      index={i}
                      total={station.parameters.length}
                      param={p}
                      options={bankParams}
                      onChange={np => updateParam(i, np)}
                      onRemove={() => removeParam(i)}
                      onMove={dir => moveParam(i, dir)}
                    />
                  ))
                )}
              </Paper>
            )}
          </Box>

          {/* RIGHT: JSON preview */}
          <Box sx={{ flex: '0 0 44%', position: 'sticky', top: 24, minWidth: 0 }}>
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden', maxHeight: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#0F172A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#94A3B8', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{tr('sb.jsonOutput', lang)}</Typography>
                <Typography sx={{ color: '#475569', fontSize: '0.68rem' }}>
                  {jsonStr.split('\n').length} {tr('sb.lines', lang)}
                </Typography>
              </Box>
              <Box
                component="pre"
                dir="ltr"
                sx={{
                  m: 0, p: 2.5,
                  overflow: 'auto',
                  flex: 1,
                  bgcolor: '#0F172A',
                  color: '#E2E8F0',
                  fontSize: '0.72rem',
                  fontFamily: '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
                  lineHeight: 1.65,
                  '&::-webkit-scrollbar': { width: 6, height: 6 },
                  '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#334155', borderRadius: 3 },
                }}
              >
                {jsonStr}
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      {/* ── STATIONS BANK TAB ── */}
      {activeTab === 1 && (
        <Box>
          {bankLoading ? (
            <Typography sx={{ color: '#94A3B8', py: 4, textAlign: 'center' }}>{tr('sb.loadingBank', lang)}</Typography>
          ) : bankStations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
              <Typography sx={{ fontSize: '0.95rem', mb: 1 }}>{tr('sb.noBank', lang)}</Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>{tr('sb.noBankHint', lang)}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {bankStations.map(bs => (
                <Paper
                  key={bs.id}
                  data-testid="bank-row"
                  elevation={0}
                  sx={{
                    border: '1px solid #E2E8F0',
                    borderLeft: `3px solid ${TYPE_COLOR[bs.stationType] ?? '#94A3B8'}`,
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: editingStationId === bs.id ? '#F8FAFF' : 'white',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>{bs.name}</Typography>
                      {isShared(bs) && (
                        <Chip label={tr('station.shared', lang)} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.63rem', height: 18 }} />
                      )}
                      <Chip
                        label={bs.stationType}
                        size="small"
                        sx={{
                          bgcolor: TYPE_COLOR[bs.stationType] ?? '#94A3B8',
                          color: '#FFF',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: 18,
                        }}
                      />
                      {(bs.infoForEvaluator?.notes || bs.infoForEvaluator?.limitation || (bs.infoForEvaluator?.includedParameters?.length ?? 0) > 0) && (
                        <Chip
                          label={tr('sb.evalInfoChip', lang)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.63rem', height: 18, borderColor: '#F59E0B', color: '#B45309' }}
                        />
                      )}
                      {bs.includeAssessorFreeScore === false && (
                        <Chip
                          label={tr('sb.noFreeScore', lang)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.63rem', height: 18, borderColor: '#94A3B8', color: '#64748B' }}
                        />
                      )}
                    </Box>
                    {bs.description && (
                      <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>{bs.description}</Typography>
                    )}
                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mt: 0.25 }}>
                      level {bs.stationLevel}
                      {bs.legacyStationId && ` · ${bs.legacyStationId}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant={editingStationId === bs.id ? 'contained' : 'outlined'}
                      startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => openBankStation(bs)}
                      sx={editingStationId === bs.id
                        ? { bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' }, fontSize: '0.75rem' }
                        : { fontSize: '0.75rem' }
                      }
                    >
                      {isShared(bs) ? tr('station.view', lang) : editingStationId === bs.id ? tr('sb.editing', lang) : tr('common.edit', lang)}
                    </Button>
                    {!isShared(bs) && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setDeleteTarget(bs)}
                        disabled={deleteMutation.isPending}
                        sx={{ fontSize: '0.75rem' }}
                      >{tr('common.delete', lang)}</Button>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr('sb.deleteTitle', lang)}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {tr('sb.deleteBodyBefore', lang)} <strong>{deleteTarget?.name}</strong> {tr('sb.deleteBodyAfter', lang)}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{tr('common.cancel', lang)}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >{tr('common.delete', lang)}</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default StationBuilder;
